<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main>
      <PageHero
        eyebrow="About us"
        title="Established since 1996,"
        subtitle="building in Singapore ever since."
        :lead="overview.intro"
        :image="heroImage.src"
        :image-alt="heroImage.alt"
        :image-caption="heroImage.caption"
      />

      <!-- ============ COMPANY OVERVIEW ============ -->
      <section class="section-pad overview-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Company overview
            </p>
            <h2 class="display-2">
              A wide range of services,
              <span>delivered to suit the actual site.</span>
            </h2>
          </div>

          <p class="lead">{{ overview.scope }}</p>
        </div>

        <div class="overview-grid">
          <article class="card overview-card">
            <div class="icon-tile"><Handshake /></div>
            <h3>How we built the business</h3>
            <p>{{ overview.experience }}</p>
          </article>

          <article class="card overview-card">
            <div class="icon-tile"><Ruler /></div>
            <h3>How we approach a project</h3>
            <p>{{ overview.approach }}</p>
          </article>
        </div>
      </section>

      <!-- ============ BCA REGISTRATIONS ============ -->
      <section id="registrations" class="registrations-section">
        <div class="shell">
          <div class="registrations-head">
            <BadgeCheck />
            <div>
              <p class="eyebrow">
                <span class="eyebrow-line"></span>
                Registrations
              </p>
              <h2 class="display-2">The company is registered with BCA under:</h2>
            </div>
          </div>

          <div class="registration-grid">
            <article v-for="item in bcaRegistrations" :key="item.code" class="registration-card">
              <strong>{{ item.code }}</strong>
              <span>{{ item.title }}</span>
            </article>
          </div>

          <CertificationMarks class="registrations-certs" label="Certifications" />

          <p class="registrations-note">
            Registered with the Building and Construction Authority of Singapore.
          </p>
        </div>
      </section>

      <!-- ============ OUR JOURNEY ============ -->
      <section id="journey" class="section-pad journey-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Our journey
            </p>
            <h2 class="display-2">
              From a small team
              <span>to main contractor scopes.</span>
            </h2>
          </div>

          <p class="lead">
            Our growth has come from doing the work, learning from each project and keeping the same
            people close to the site.
          </p>
        </div>

        <ol class="journey">
          <li v-for="(item, index) in journey" :key="item.stage">
            <div class="journey-index">
              <span>{{ String(index + 1).padStart(2, "0") }}</span>
            </div>

            <div class="journey-content">
              <span class="journey-stage">{{ item.stage }}</span>
              <h3>{{ item.heading }}</h3>
              <p>{{ item.body }}</p>
            </div>
          </li>
        </ol>
      </section>

      <!-- ============ CTA ============ -->
      <SectionCta
        eyebrow="Work with us"
        heading="Have a building or project that needs attention?"
        body="Send us the scope, the site conditions and any photos. We will review the requirements and recommend a practical approach."
      />
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { BadgeCheck, Handshake, Ruler } from "lucide-vue-next";

import CertificationMarks from "@/components/CertificationMarks.vue";
import PageHero from "@/components/PageHero.vue";
import SectionCta from "@/components/SectionCta.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import { bcaRegistrations, journey, overview } from "@/content/company-content";
import { pageHeroes } from "@/content/gallery";

export default {
  name: "AboutPage",

  components: {
    BadgeCheck,
    CertificationMarks,
    Handshake,
    PageHero,
    Ruler,
    SectionCta,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      overview,
      bcaRegistrations,
      journey,
      heroImage: pageHeroes.about,
    };
  },
};
</script>

<style scoped>
.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 52px;
}

.overview-card {
  padding: 32px;
}

.overview-card h3 {
  margin: 24px 0 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 23px;
  line-height: 1.22;
  letter-spacing: -0.035em;
}

.overview-card p {
  margin: 16px 0 0;
  color: var(--muted);
  font-size: 14.5px;
  line-height: 1.85;
}

/* ---------- registrations ---------- */

.registrations-section {
  position: relative;
  z-index: 2;
  padding: 104px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #eef4fb;
}

.registrations-head {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 20px;
  align-items: start;
}

.registrations-head > svg {
  width: 44px;
  height: 44px;
  color: var(--blue);
}

.registration-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 44px;
}

.registration-card {
  padding: 28px;
  border: 1px solid var(--line);
  border-top: 3px solid var(--blue);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.registration-card strong {
  display: block;
  color: var(--blue);
  font-family: var(--font-display);
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.registration-card span {
  display: block;
  margin-top: 12px;
  color: var(--ink-soft);
  font-size: 13.5px;
  line-height: 1.6;
}

.registrations-certs {
  margin-top: 40px;
}

.registrations-note {
  margin: 22px 0 0;
  color: var(--muted-light);
  font-size: 12px;
}

/* ---------- journey ---------- */

.journey {
  margin: 56px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: journey;
}

.journey li {
  position: relative;
  display: grid;
  grid-template-columns: 78px 1fr;
  gap: 28px;
  padding-bottom: 44px;
}

.journey li:last-child {
  padding-bottom: 0;
}

/* vertical connector */
.journey li::before {
  content: "";
  position: absolute;
  top: 56px;
  bottom: 0;
  left: 27px;
  width: 1px;
  background: #cfdeeb;
}

.journey li:last-child::before {
  display: none;
}

.journey-index {
  position: relative;
  z-index: 2;
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.journey-index span {
  color: var(--blue);
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.journey-content {
  padding-top: 4px;
  max-width: 860px;
}

.journey-stage {
  display: block;
  color: var(--muted-light);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.journey-content h3 {
  margin: 12px 0 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.journey-content p {
  margin: 14px 0 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.85;
}

@media (max-width: 1080px) {
  .overview-grid,
  .registration-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .overview-card {
    padding: 26px;
  }

  .registrations-section {
    padding: 76px 0;
  }

  .registrations-head {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .registration-card {
    padding: 24px;
  }

  .journey li {
    grid-template-columns: 48px 1fr;
    gap: 18px;
    padding-bottom: 34px;
  }

  .journey li::before {
    top: 46px;
    left: 22px;
  }

  .journey-index {
    width: 44px;
    height: 44px;
  }

  .journey-content h3 {
    font-size: 22px;
  }
}
</style>
